import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AssessmentTabs } from "@/components/assessment/AssessmentTabs";
import { ResultsFilters } from "@/components/assessment/results/ResultsFilters";
import { TakeawayCards } from "@/components/assessment/results/TakeawayCards";
import { SiteMap } from "@/components/assessment/results/SiteMap";
import { RiskMatrixPanel } from "@/components/assessment/results/RiskMatrixPanel";
import { HazardGaugeRow } from "@/components/assessment/results/HazardGaugeRow";
import { HazardRiskBarChart } from "@/components/assessment/results/HazardRiskBarChart";
import { ValueChainRiskPanel } from "@/components/assessment/results/ValueChainRiskPanel";
import { HorizonComparisonGrid } from "@/components/assessment/results/HorizonComparisonGrid";
import { getResults, HORIZON_LABELS, type AssetHazardResult } from "@/lib/results";
import {
  getWorkspaceValueChainImportance,
  listAssets,
  listValueChainStages,
  type Asset,
  type ValueChainStage,
} from "@/lib/assets";
import {
  defaultResultsFilterState,
  filterAssets,
  filterResults,
  getResultsForHorizon,
  orderedHazards,
  orderedHorizons,
  type ResultsFilterState,
} from "@/lib/results-filters";
import type { TimeHorizon } from "@shared/scoring/types";

function AssessmentResults() {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [stages, setStages] = useState<ValueChainStage[]>([]);
  const [stageImportance, setStageImportance] = useState<Record<string, number>>({});
  const [results, setResults] = useState<AssetHazardResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ResultsFilterState>(defaultResultsFilterState());
  const [mapHorizon, setMapHorizon] = useState<TimeHorizon>("historical");

  useEffect(() => {
    Promise.all([listAssets(), listValueChainStages(), getWorkspaceValueChainImportance(), getResults()])
      .then(([assetsRes, stagesRes, importanceRes, resultsRes]) => {
        setAssets(assetsRes.assets);
        setStages(stagesRes.stages);
        const map: Record<string, number> = {};
        for (const rating of importanceRes.ratings) map[rating.valueChainStageId] = rating.importance;
        setStageImportance(map);
        setResults(resultsRes.results);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load results"));
  }, []);

  if (error) return <p className="text-sm font-medium text-destructive">{error}</p>;
  if (!assets) return <p className="text-sm text-muted-foreground">Loading...</p>;

  const filteredAssets = filterAssets(assets, filters, stageImportance);
  const filteredAssetIds = new Set(filteredAssets.map((a) => a.id));
  const filteredResults = filterResults(results, filters, filteredAssetIds);
  // The bar chart and gauge row enumerate hazards from a constant, not from
  // their rows, so they need the selection itself — filtering the data alone
  // would leave them rendering empty slots for deselected hazards.
  const activeHazards = orderedHazards(filters.hazards);

  const activeHorizons = orderedHorizons(filters.horizons);
  // activeHorizons[0] is safe only because it can never be empty: the
  // Horizon filter defaults to ["historical"] and ResultsFilters.tsx
  // (Task 6) refuses to let the last chip be deselected. If that invariant
  // ever changes, this needs an explicit fallback -- TS won't catch it
  // (noUncheckedIndexedAccess isn't enabled in this project).
  const effectiveMapHorizon = activeHorizons.includes(mapHorizon) ? mapHorizon : activeHorizons[0];

  const horizonSummary = activeHorizons.map((h) => HORIZON_LABELS[h]).join(" vs ");

  return (
    <div className="flex flex-col gap-6">
      <AssessmentTabs />

      <ResultsFilters filters={filters} onChange={setFilters} assets={assets} stages={stages} />

      <div className="flex flex-col gap-0.5">
        <h1 className="text-xl font-heading font-bold leading-7">Results overview</h1>
        <p className="text-xs leading-4 text-muted-foreground">
          Gross risk before adaptation measures — {filters.scenario} · {horizonSummary}
        </p>
      </div>

      <HorizonComparisonGrid horizons={filters.horizons} scenario={filters.scenario} results={filteredResults}>
        {(resultsForHorizon) => (
          <TakeawayCards results={resultsForHorizon} assets={filteredAssets} stageImportance={stageImportance} />
        )}
      </HorizonComparisonGrid>

      <SiteMap
        assets={filteredAssets}
        results={getResultsForHorizon(filteredResults, effectiveMapHorizon, filters.scenario)}
        horizon={effectiveMapHorizon}
        activeHorizons={activeHorizons}
        onHorizonChange={setMapHorizon}
      />

      <HorizonComparisonGrid horizons={filters.horizons} scenario={filters.scenario} results={filteredResults}>
        {(resultsForHorizon) => <HazardRiskBarChart results={resultsForHorizon} hazards={activeHazards} />}
      </HorizonComparisonGrid>

      <HorizonComparisonGrid horizons={filters.horizons} scenario={filters.scenario} results={filteredResults}>
        {(resultsForHorizon) => <RiskMatrixPanel results={resultsForHorizon} assets={filteredAssets} />}
      </HorizonComparisonGrid>

      <HorizonComparisonGrid horizons={filters.horizons} scenario={filters.scenario} results={filteredResults}>
        {(resultsForHorizon) => <ValueChainRiskPanel assets={filteredAssets} stages={stages} results={resultsForHorizon} />}
      </HorizonComparisonGrid>

      <HorizonComparisonGrid horizons={filters.horizons} scenario={filters.scenario} results={filteredResults}>
        {(resultsForHorizon) => <HazardGaugeRow results={resultsForHorizon} hazards={activeHazards} />}
      </HorizonComparisonGrid>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <Button variant="outline" asChild>
          <Link href="/assessment">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Input Data
          </Link>
        </Button>
        <Button asChild>
          <Link href="/assessment/detailed">
            See detailed results
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default AssessmentResults;
